// @flow

import { toAtomicProgramNames } from '../../../../utils/affordable';

/**
 * Returns the YYYY-MM-DD date string for a UTC ISO string in the given timezone.
 * e.g. "2026-05-13T05:00:00.000Z" in "America/Chicago" (CDT, UTC-5) → "2026-05-13"
 */
export function toDateInTz(isoStr: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(
    new Date(isoStr),
  );
}

/**
 * Converts a YYYY-MM-DD string to a UTC ISO string representing midnight
 * in the given property timezone.
 * e.g. "2026-05-13" + "America/Chicago" (CDT, UTC-5) → "2026-05-13T05:00:00.000Z"
 *
 * Two-step approach to handle DST transition dates correctly:
 *   1. Use noon UTC to get a rough UTC offset and derive a candidate midnight.
 *   2. Check what the TZ wall-clock shows at that candidate and correct for any
 *      DST-induced discrepancy (e.g. spring-forward: noon is CDT but midnight is
 *      still CST, so a noon-only estimate is off by 1 hour and shows the wrong date).
 */
export function startOfDayInTz(dateStr: string, timezone: string): string {
  const tzMinutesAt = (utcMs: number): number => {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(utcMs));
    const h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0');
    const m = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0');
    return h * 60 + m;
  };

  // Step 1: rough candidate using noon UTC offset
  const noonUtcMs = new Date(`${dateStr}T12:00:00Z`).getTime();
  const roughOffsetMs = (720 - tzMinutesAt(noonUtcMs)) * 60_000;
  const candidateMs =
    new Date(`${dateStr}T00:00:00Z`).getTime() + roughOffsetMs;

  // Step 2: refine — wall-clock at candidate should be 00:00; adjust if DST shifted it
  const wallMinutes = tzMinutesAt(candidateMs);
  const correctionMs =
    wallMinutes < 720
      ? -wallMinutes * 60_000 // e.g. showing 01:00 → subtract 1 h
      : (1440 - wallMinutes) * 60_000; // e.g. showing 23:00 → add 1 h

  return new Date(candidateMs + correctionMs).toISOString();
}

/**
 * An open waitlist needs a scheduled open date. Single home for the rule —
 * read by the section for the field's error text and by the page's Save gate,
 * which additionally requires the user to have changed one of these fields.
 */
export function isOpenDateMissing(
  enabled: boolean,
  isOpen: boolean,
  openDate: ?string,
): boolean {
  return Boolean(enabled && isOpen && !openDate);
}

/**
 * The atomic program names a waitlist can be configured with, from the
 * property's affordable program rows.
 */
export function getWaitlistProgramNames(
  propertyAffordablePrograms: ?$ReadOnlyArray<Object>,
): Array<string> {
  return toAtomicProgramNames(
    (propertyAffordablePrograms ?? []).map(
      (program) => program?.masterAffordableProgram?.name,
    ),
  );
}

/**
 * The waitlist's programs as the user sees and saves them. A field the user
 * never touched means every program on the property; an explicit [] means they
 * cleared it and is kept. The default is stored rather than derived on read
 * because backend readers query the configuration's program rows directly.
 */
export function resolveWaitlistPrograms(
  programs: ?$ReadOnlyArray<string>,
  propertyAffordablePrograms: ?$ReadOnlyArray<Object>,
): $ReadOnlyArray<string> {
  return Array.isArray(programs)
    ? programs
    : getWaitlistProgramNames(propertyAffordablePrograms);
}

/**
 * Builds the waitlistV2 slice of the property save payload. `programs` goes
 * through resolveWaitlistPrograms so the save stores what the section displays.
 */
export function buildWaitlistV2Payload(
  setup: ?Object,
  propertyAffordablePrograms: ?$ReadOnlyArray<Object>,
): Object {
  const waitlistV2 = setup?.waitlistV2;
  return {
    programs: resolveWaitlistPrograms(
      waitlistV2?.programs,
      propertyAffordablePrograms,
    ),
    scheduledOpenDate: waitlistV2?.scheduledOpenDate ?? null,
    scheduledCloseDate: waitlistV2?.scheduledCloseDate ?? null,
    separateByBedroom: waitlistV2?.separateByBedroom ?? false,
    usePreferencesForRanking: waitlistV2?.usePreferencesForRanking ?? false,
    preferenceWeightMethod: waitlistV2?.preferenceWeightMethod ?? null,
    preferences: waitlistV2?.preferences ?? [],
  };
}
